import * as Yup from "yup";
import { PHONE_REGEX } from "../../Pages/Contacts/bulk-contact-import/validation";
import { dynamicExampleMatchesPrefix } from "../../helpers/dynamic-url-cta-buttons";

const varRegex = /\{\{([^{}]+)\}\}/g;
const digitRegex = /^\d+$/;
const emojiRegex = /\p{Emoji_Presentation}/gu;

export const templateMessageValidationSchema = Yup.object().shape({
	name: Yup.string()
		.matches(/^[0-9a-z-_]{5,}$/, "Invalid name")
		.required("Name is required"),
	category: Yup.object().required("Category is required"),
	type: Yup.object().required("Message type is required"),
	attachment: Yup.object().required(
		"Select 'No attachments' if not required"
	),
	language: Yup.object().required("Language is required"),
	text: Yup.string()
		.required("Message text is required")
		.min(5, "Template must be more than 5 characters long")
		.max(1000, "Template must be less than 1000 characters long")
		.test(
			"doesnt end or start with variable",
			"Cannot start or end with variable",
			(val) => {
				if (!val) return true;
				const trimmed = val.trim();
				if (/^\{\{\d+\}\}/.test(trimmed)) return false;
				if (/\{\{\d+\}\}$/.test(trimmed)) return false;
				return true;
			}
		)
		.test(
			"test var values correct",
			"Variables should consist of *only* a number between {{ and }}",
			(val) => {
				let allVars = [...val.matchAll(varRegex)].map((m) => m[1]);
				const hasInvalid = allVars.some((v) => !digitRegex.test(v));
				if (hasInvalid) return false;

				return true;
			}
		)
		.test(
			"test vars ascending",
			"Variables should be in ascending order, e.g. 'Hi {{1}}, your order {{2}} has shipped...'",
			(val) => {
				//return false for a fail condition
				let allVars = [...val.matchAll(varRegex)].map((m) => m[1]);
				console.log(allVars, `< allVars`);

				for (let i = 1; i < allVars.length; i++) {
					if (Number(allVars[i]) < Number(allVars[i - 1])) {
						return false;
					}
				}
				return true;
			}
		)
		.test(
			"emoji test",
			"Templates with more than 10 emoji's will be rejected by Meta",
			(val) => {
				const stripVariables = (str) => str.replace(/\{\{.*?\}\}/g, "");

				function countEmojis(str) {
					const cleaned = stripVariables(str);
					const matches = cleaned.match(emojiRegex);
					return matches ? matches.length : 0;
				}
				const count = countEmojis(val);
				if (count > 10) return false;
				return true;
			}
		)
		.test(
			"spaces and tab test",
			"Tabs or more than 4 consecutive spaces will be rejected by Meta",
			(val) => {
				console.log(val, `< this is val`);

				const hasTabs = /[\r\t]/.test(val);
				const hasTooManySpaces = / {5,}/.test(val);

				return !hasTabs && !hasTooManySpaces;
			}
		)

		.test(
			">1 consecutive empty lines test",
			"Templates with more than one consecutive line break may be rejected by Meta.",
			(val) => {
				if (!val) return true;

				// Normalize Windows line endings
				const normalized = val.replace(/\r\n/g, "\n");

				// Detect 2+ empty (or whitespace-only) lines in a row
				const hasTooManyEmptyLines = /\n\s*\n\s*\n/.test(normalized);

				console.log(
					hasTooManyEmptyLines,
					"< has too many empty lines?"
				);
				return !hasTooManyEmptyLines;
			}
		),
	account: Yup.array()
		.min(1, "Account Required")
		.required("Account is required"),

	exampleMessage: Yup.string()
		.required("Example message is required")
		.test(
			"are the same",
			"Message Text and Example Message do not correctly match, ensure template example is using [ ] for placeholders",
			function (example) {
				const { text } = this.parent;

				const regEx = /{{.*?}}/g;
				const newText = text?.replaceAll(regEx, "X");
				const exampleRegEx = /\[.*?\]/g;
				const newExample = example?.replaceAll(exampleRegEx, "X");

				const numberInBracketsRegEx = /\[\d+\]/g;
				const containsNumbersInBrackets =
					numberInBracketsRegEx.test(example);
				if (containsNumbersInBrackets) return false;

				return newText === newExample;
			}
		),
	quickReplyButtons: Yup.array().test(
		"Has All Values",
		"Titles Cannot be empty",
		function (example) {
			return example.filter((item) => !item.title).length == 0;
		}
	),
	callToActionButtons: Yup.array()
		.test("Has All Values", "Titles Cannot be empty", function (example) {
			return example.filter((item) => !item.title).length == 0;
		})
		.test(
			"Numbers cant be false",
			"Numbers must be valid",
			function (example) {
				let invalidPhones = example.filter((item) => {
					return (
						item.type === "phone" && !PHONE_REGEX.test(item.phone)
					);
				});
				return invalidPhones.length === 0;
			}
		)
		.test("URLs cant be false", "URLs must be valid", function (example) {
			let invalidURLs = example.filter((item) => {
				if (item.type === "staticURL") {
					return !URL_REGEX.test(item.url);
				}
				if (item.type === "dynamicURL") {
					const base = (item.url || "").replace(/\{\{1\}\}$/, "sample");
					return !URL_REGEX.test(base);
				}
				return false;
			});
			return invalidURLs.length === 0;
		})
		.test(
			"Dynamic URLs need a placeholder",
			"Dynamic URLs must end with a single placeholder, e.g. mywebsite.com/{{1}}",
			function (example) {
				let invalidPlaceholders = example.filter((item) => {
					if (item.type !== "dynamicURL") return false;
					const url = item.url || "";
					const placeholders = url.match(/\{\{\d+\}\}/g) || [];
					return (
						placeholders.length !== 1 || !/\{\{1\}\}$/.test(url)
					);
				});
				return invalidPlaceholders.length === 0;
			}
		)
		.test(
			"Dynamic URL example required",
			"Enter a full sample URL for each dynamic URL button",
			function (example) {
				const missing = example.filter(
					(item) =>
						item.type === "dynamicURL" &&
						!(item.exampleUrl && item.exampleUrl.trim())
				);
				return missing.length === 0;
			}
		)
		.test(
			"Dynamic URL example valid",
			"Sample URL must be a valid full URL matching the dynamic prefix",
			function (example) {
				const invalid = example.filter((item) => {
					if (item.type !== "dynamicURL") return false;
					if (!URL_REGEX.test(item.exampleUrl || "")) return true;
					return !dynamicExampleMatchesPrefix(
						item.url,
						item.exampleUrl
					);
				});
				return invalid.length === 0;
			}
		),
});

;
export const URL_REGEX =
	/[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{2,63}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/i